// Full BP Agency service catalog - mirrors www.bp.agency
// 24 services across 4 categories with sub-services + process steps.

export const SERVICE_CATEGORIES = [
  {
    key: 'web',
    title: 'Web & Development',
    blurb: 'Build your digital foundation with professional website design, reliable hosting, and custom development.',
  },
  {
    key: 'marketing',
    title: 'Digital Marketing',
    blurb: 'Drive growth with comprehensive digital marketing services. From SEO to paid advertising, we have you covered.',
  },
  {
    key: 'creative',
    title: 'Creative Services',
    blurb: 'Professional photography, videography, graphic design, and branding to elevate your visual identity.',
  },
  {
    key: 'growth',
    title: 'Business & Growth',
    blurb: 'Strategic consulting, growth hacking, and IT services to help your business scale efficiently.',
  },
];

const PROCESS_DEFAULT = [
  { n: 1, title: 'Discovery & Audit', body: 'We listen, audit your current setup, study your market, and surface the real opportunities.' },
  { n: 2, title: 'Strategy', body: 'A custom plan with clear scope, KPIs, and milestones - written in plain English, not jargon.' },
  { n: 3, title: 'Execute', body: 'Our in-house team ships - design, development, content, and campaigns - on time and on budget.' },
  { n: 4, title: 'Report & Refine', body: 'Monthly reporting with transparent metrics and continuous optimization to maximize ROI.' },
];

const STAT_BAR = [
  { v: '19+', l: 'Years Experience' },
  { v: '1k+', l: 'Projects Delivered' },
  { v: 'Top 25', l: 'Voted Agency' },
  { v: '100%', l: 'Client Focused' },
];

export const SERVICES = [
  // ── WEB & DEVELOPMENT ─────────────────────────────────────────
  {
    slug: 'website-design-redesign', category: 'web',
    name: 'Website Design & Redesign',
    short: 'Conversion-focused websites built to generate leads, trust, and revenue.',
    hero: "Beautiful, fast, lead-driving websites for businesses of every size. We design and build sites people actually want to use - and Google loves to rank.",
    image: '/images/blog/photo-1559028012-481c04fa702d.jpg',
    offerings: [
      { t: 'Discovery & Strategy', b: 'Audience research, sitemap planning, and goal mapping before a single pixel is moved.' },
      { t: 'UX & Wireframing', b: 'Conversion-focused layouts engineered to move visitors through your funnel.' },
      { t: 'Custom Visual Design', b: 'Distinctive, on-brand designs that work as hard as your sales team.' },
      { t: 'Development & QA', b: 'Hand-crafted code, Core Web Vitals optimized, accessible, and tested everywhere.' },
      { t: 'CMS Training', b: 'You stay in control. We train your team to update content without touching code.' },
      { t: 'Launch & Support', b: 'Smooth go-lives, redirect mapping, analytics setup, and ongoing care plans.' },
    ],
    benefits: ['Conversion-focused design and copywriting', 'Mobile-first, accessible (WCAG 2.1 AA)', 'Core Web Vitals optimized for SEO', 'WordPress, custom code, or headless', '19 years of award-winning website experience'],
  },
  {
    slug: 'wordpress-development', category: 'web',
    name: 'WordPress Development',
    short: 'Custom WordPress builds with scalable architecture, SEO-friendly structure, and easy content management.',
    hero: 'Custom WordPress development that doesn\'t feel like WordPress. Fast, secure, and easy for your team to manage long after launch day.',
    image: '/images/blog/photo-1547658719-da2b51169166.jpg',
    offerings: [
      { t: 'Custom Theme Development', b: 'Built from scratch - no bloated templates, no junk plugins, no compromises.' },
      { t: 'Plugin Customization', b: 'Tailored functionality for booking, payments, integrations, and more.' },
      { t: 'WooCommerce Stores', b: 'High-performance eCommerce with conversion-optimized PDPs and checkout.' },
      { t: 'Maintenance & Security', b: 'Updates, backups, malware scanning, and 24/7 uptime monitoring.' },
      { t: 'Speed Optimization', b: 'Caching, CDN, image optimization - Core Web Vitals tuned for SEO.' },
      { t: 'Migration & Hosting', b: 'Seamless migrations to managed hosting with zero downtime.' },
    ],
    benefits: ['Custom-coded themes - no bloated builders', 'Scalable architecture for growing sites', 'SEO-friendly structure and schema', 'Easy content editing with Gutenberg/ACF', 'Managed maintenance plans available'],
  },
  {
    slug: 'ecommerce-development', category: 'web',
    name: 'eCommerce Development',
    short: 'Revenue-driven online stores with optimized checkout, product pages, and integrations.',
    hero: 'eCommerce sites engineered to sell. Beautiful storefronts, frictionless checkout, and the integrations you need to scale.',
    image: '/images/blog/photo-1556742049-0cfed4f6a45d.jpg',
    offerings: [
      { t: 'Platform Selection', b: 'WooCommerce, Shopify, BigCommerce - we recommend what fits your model.' },
      { t: 'Storefront Design', b: 'Conversion-driven design with strong PDPs, social proof, and trust signals.' },
      { t: 'Checkout Optimization', b: 'Streamlined, one-page checkouts that lower cart abandonment.' },
      { t: 'Payments & Shipping', b: 'Stripe, PayPal, Klarna, real-time shipping rates, and tax automation.' },
      { t: 'ERP & CRM Integration', b: 'Connect your store to QuickBooks, NetSuite, Salesforce, and more.' },
      { t: 'Growth Analytics', b: 'GA4, server-side tracking, and revenue attribution dashboards.' },
    ],
    benefits: ['Optimized checkout (lower cart abandonment)', 'Mobile-first product experiences', 'ERP, CRM, and shipping integrations', 'Subscription, B2B, and marketplace support', 'PCI-compliant, secure payments'],
  },
  {
    slug: 'custom-programming', category: 'web',
    name: 'Custom Programming',
    short: 'Custom solutions including CRM integrations, APIs, dashboards, and automation workflows.',
    hero: 'When off-the-shelf doesn\'t cut it. Custom software, integrations, and automation built by senior engineers.',
    image: '/images/blog/photo-1542831371-29b0f74f9713.jpg',
    offerings: [
      { t: 'API Development', b: 'REST and GraphQL APIs for web, mobile, and third-party integrations.' },
      { t: 'CRM Integrations', b: 'Salesforce, HubSpot, Zoho - connect your website to your sales process.' },
      { t: 'Custom Dashboards', b: 'Internal tooling and reporting dashboards with role-based access.' },
      { t: 'Workflow Automation', b: 'Zapier, Make, and custom automation to eliminate manual work.' },
      { t: 'Web Apps', b: 'Full-stack web applications built with modern frameworks.' },
      { t: 'Data Migrations', b: 'Clean, validated data migrations between platforms.' },
    ],
    benefits: ['Senior US-based engineers', 'Clean, documented, maintainable code', 'Modern stacks: React, Node, Python', 'Secure, scalable architecture', 'Long-term support and SLAs'],
  },
  {
    slug: 'website-mail-hosting', category: 'web',
    name: 'Website & Mail Hosting',
    short: 'Secure, fast hosting with uptime monitoring, backups, and managed support.',
    hero: 'Managed hosting that just works. Fast servers, automatic backups, expert support - and a phone number for when you need a human.',
    image: '/images/blog/photo-1558494949-ef010cbdcc31.jpg',
    offerings: [
      { t: 'Managed WordPress Hosting', b: 'High-performance hosting with caching, CDN, and one-click staging.' },
      { t: 'Business Email', b: 'Google Workspace, Microsoft 365, and custom email with spam protection.' },
      { t: 'SSL & Security', b: 'Free SSL, web application firewall, and DDoS mitigation included.' },
      { t: 'Daily Backups', b: 'Automatic daily backups with 30-day retention and one-click restore.' },
      { t: 'Uptime Monitoring', b: '24/7 monitoring with proactive alerts and rapid response.' },
      { t: 'Expert Support', b: 'Real humans, real phone numbers, real response times.' },
    ],
    benefits: ['99.9%+ uptime SLA', 'Daily backups with point-in-time restore', 'Free SSL and DDoS protection', 'CDN included for global speed', 'US-based support team'],
  },

  // ── DIGITAL MARKETING ─────────────────────────────────────────
  {
    slug: 'search-engine-optimization', category: 'marketing',
    name: 'Search Engine Optimization (SEO)',
    short: 'Intent-based SEO including keyword targeting, technical SEO, local SEO, and authority building.',
    hero: 'Dominate Google search results with our proven SEO strategies. We deliver intent-based optimization that combines keyword targeting, technical SEO, local SEO, and authority building to drive qualified organic traffic to your website. Our white-hat methods have helped hundreds of businesses achieve top rankings and sustainable growth.',
    image: '/images/blog/photo-1686061594225-3e92c0cd51b0.jpg',
    featured: true,
    offerings: [
      { t: 'Keyword Research', b: 'Deep keyword analysis targeting high-intent search terms your customers are actually using to find services like yours.' },
      { t: 'Technical SEO', b: 'Site speed optimization, crawlability fixes, structured data, and Core Web Vitals improvements for better rankings.' },
      { t: 'Local SEO', b: 'Google Business Profile optimization, local citations, and geo-targeted strategies to dominate local search results.' },
      { t: 'Authority Building', b: 'Strategic link building, digital PR, and content partnerships to grow your domain authority and trustworthiness.' },
      { t: 'Content Strategy', b: 'SEO-driven content creation that answers customer questions, builds topical authority, and captures featured snippets.' },
      { t: 'Analytics & Reporting', b: 'Monthly performance reports with clear KPIs, ranking improvements, and ROI tracking you can understand.' },
    ],
    benefits: ['Average 150%+ increase in organic traffic', 'White-hat methods for sustainable, long-term results', 'Transparent monthly reporting with clear ROI metrics', 'Dedicated SEO specialist assigned to your account', '19 years of proven SEO experience and results'],
    process: [
      { n: 1, title: 'SEO Audit', body: 'Comprehensive technical and content audit to identify opportunities and competitive gaps.' },
      { n: 2, title: 'Strategy', body: 'Custom SEO roadmap with keyword targets, content calendar, and link building plan.' },
      { n: 3, title: 'Execute', body: 'On-page optimization, content creation, technical fixes, and authority building campaigns.' },
      { n: 4, title: 'Report & Refine', body: 'Monthly reporting with data-driven adjustments to maximize ranking improvements.' },
    ],
    extraStats: [{ v: '+150%', l: 'Avg. Traffic' }, { v: 'Top 10', l: 'Rankings' }, { v: '500K+', l: 'Leads' }, { v: '1k+', l: 'Websites Built' }],
  },
  {
    slug: 'ai-optimization-aeo', category: 'marketing',
    name: 'AI Optimization (AEO)',
    short: 'Optimization for AI platforms like ChatGPT and Gemini using structured content and prompt targeting.',
    hero: 'Be the answer in ChatGPT, Gemini, and Perplexity. AEO (Answer Engine Optimization) makes your brand the citation when AI assistants answer questions in your category.',
    image: '/images/blog/photo-1677442136019-21780ecad995.jpg',
    offerings: [
      { t: 'Entity Optimization', b: 'Build the entity graph so AI assistants understand who you are and what you do.' },
      { t: 'Structured Content', b: 'Schema markup, FAQ pages, and structured data that AI engines can parse and cite.' },
      { t: 'Prompt Research', b: 'Identify the prompts your buyers actually ask - and engineer content to win them.' },
      { t: 'Citation Building', b: 'Earn mentions on the high-authority sources AI assistants pull from.' },
      { t: 'Brand Knowledge Graph', b: 'Wikipedia, Wikidata, and knowledge panel optimization.' },
      { t: 'Monitoring & Attribution', b: 'Track citation share across ChatGPT, Perplexity, Gemini, and Claude.' },
    ],
    benefits: ['Get cited as the AI answer in your category', 'Reach buyers researching outside Google', 'Compounds with traditional SEO', 'First-mover advantage in your niche', 'Measurable citation tracking'],
  },
  {
    slug: 'pay-per-click-marketing', category: 'marketing',
    name: 'Pay-Per-Click Marketing (PPC)',
    short: 'Google Ads, LSAs, and high-intent paid campaigns focused on ROI and conversions.',
    hero: 'PPC campaigns built for ROI, not impressions. We manage Google Ads, LSAs, Meta, and YouTube - every dollar tracked, every conversion attributed.',
    image: '/images/blog/photo-1551288049-bebda4e38f71.jpg',
    offerings: [
      { t: 'Google Search Ads', b: 'High-intent campaigns targeting the keywords buyers actually use.' },
      { t: 'Local Services Ads (LSAs)', b: 'Pay-per-lead campaigns for home services, healthcare, and pros.' },
      { t: 'YouTube & Display', b: 'Video and display campaigns built for awareness and remarketing.' },
      { t: 'Performance Max', b: 'AI-driven campaigns across all Google inventory.' },
      { t: 'Landing Pages', b: 'Dedicated, fast-loading landing pages designed to convert.' },
      { t: 'Attribution', b: 'Server-side tracking and CRM integration for true ROI.' },
    ],
    benefits: ['Google Premier Partner certified', 'Daily campaign management', 'Conversion-grade landing pages included', 'Real-time dashboards and call tracking', 'No long-term contracts'],
  },
  {
    slug: 'social-media-marketing', category: 'marketing',
    name: 'Social Media Marketing',
    short: 'Content, engagement, and paid strategies across Facebook, Instagram, and LinkedIn.',
    hero: 'Social media that builds a brand, not just a follower count. Strategy, content, community, and paid - done right.',
    image: '/images/blog/photo-1611162617474-5b21e879e113.jpg',
    offerings: [
      { t: 'Content Strategy', b: 'Editorial calendars built around your audience, not trends.' },
      { t: 'Content Production', b: 'Photo, video, and graphics produced by an in-house team.' },
      { t: 'Community Management', b: 'Real humans engaging with your audience, daily.' },
      { t: 'Paid Social', b: 'Meta, LinkedIn, and TikTok ads tied to revenue.' },
      { t: 'Influencer Partnerships', b: 'Vetted creator partnerships that fit your brand.' },
      { t: 'Reporting & Insights', b: 'Engagement, reach, and pipeline impact - measured monthly.' },
    ],
    benefits: ['In-house content production team', 'Consistent posting, consistent voice', 'Paid + organic strategy under one roof', 'Real engagement, not vanity metrics', 'Monthly reporting tied to business KPIs'],
  },
  {
    slug: 'video-display-marketing', category: 'marketing',
    name: 'Video & Display Marketing',
    short: 'YouTube, display ads, and visual storytelling campaigns.',
    hero: 'Tell your story at scale. YouTube, OTT/CTV, and programmatic display campaigns engineered to build brand and demand.',
    image: '/images/blog/photo-1485846234645-a62644f84728.jpg',
    offerings: [
      { t: 'YouTube Advertising', b: 'TrueView, bumper, and shorts campaigns for awareness and conversion.' },
      { t: 'OTT/CTV', b: 'Connected TV ads on Hulu, Roku, and streaming inventory.' },
      { t: 'Programmatic Display', b: 'Audience-targeted display across the open web.' },
      { t: 'Video Production', b: 'Concept, script, shoot, edit - all in-house.' },
      { t: 'Creative Testing', b: 'Multivariate ad testing to find your highest performers.' },
      { t: 'Brand Lift Studies', b: 'Measure awareness, recall, and consideration impact.' },
    ],
    benefits: ['Full-funnel video strategy', 'In-house production team', 'Cross-channel attribution', 'CTV + YouTube + programmatic in one plan', 'Brand lift measurement'],
  },
  {
    slug: 'email-marketing', category: 'marketing',
    name: 'Email Marketing',
    short: 'Automated campaigns, nurturing sequences, and CRM integrations.',
    hero: 'Email is the highest-ROI channel - when it\'s done right. Lifecycle programs, automated nurtures, and CRM integrations that turn leads into customers.',
    image: '/images/blog/photo-1596526131083-e8c633c948d2.jpg',
    offerings: [
      { t: 'Lifecycle Strategy', b: 'Welcome series, nurture flows, win-back, and reactivation programs.' },
      { t: 'Newsletter Production', b: 'Branded weekly/monthly newsletters with content your audience opens.' },
      { t: 'eCommerce Email', b: 'Klaviyo flows for cart abandon, post-purchase, and replenishment.' },
      { t: 'B2B Nurture Tracks', b: 'Lead scoring and nurture sequences integrated with your CRM.' },
      { t: 'Deliverability', b: 'SPF, DKIM, DMARC, list hygiene - inboxed, not spam-folder\'d.' },
      { t: 'A/B Testing', b: 'Subject lines, send times, content - tested, measured, and scaled.' },
    ],
    benefits: ['Higher open and click rates', 'Real revenue attribution', 'Klaviyo, HubSpot, Mailchimp expertise', 'Deliverability monitoring included', 'GDPR/CAN-SPAM compliant'],
  },
  {
    slug: 'competitor-marketing', category: 'marketing',
    name: 'Competitor Marketing',
    short: 'Targeting competitor keywords and positioning strategies.',
    hero: 'Show up where your competitors are losing. Conquesting campaigns, comparison content, and positioning that wins consideration.',
    image: '/images/blog/photo-1460925895917-afdab827c52f.jpg',
    offerings: [
      { t: 'Competitor SEO', b: 'Rank for "[competitor] alternative" and "vs" queries.' },
      { t: 'Conquest PPC', b: 'Bidding strategically on competitor brand terms.' },
      { t: 'Comparison Pages', b: 'Honest, well-built comparison pages that earn rankings and conversions.' },
      { t: 'Positioning Strategy', b: 'Differentiation work - what makes you the better choice and how to say it.' },
      { t: 'Win-Back Campaigns', b: 'Targeting unhappy competitor customers via search and social.' },
      { t: 'Review Acquisition', b: 'Earn reviews where competitors are losing them.' },
    ],
    benefits: ['Steal high-intent traffic from competitors', 'Win comparison shopper visibility', 'Defensive + offensive strategy', 'Sales-team enablement assets included', 'Ethical, white-hat tactics only'],
  },
  {
    slug: 'remarketing-programs', category: 'marketing',
    name: 'Remarketing Programs',
    short: 'Retargeting visitors to increase conversions and reduce CPA.',
    hero: 'Most visitors don\'t convert on the first visit. Remarketing brings them back - across search, social, and display - at a fraction of the CAC.',
    image: '/images/blog/photo-1432888622747-4eb9a8efeb07.jpg',
    offerings: [
      { t: 'Audience Segmentation', b: 'Smart audiences based on behavior, intent, and funnel stage.' },
      { t: 'Cross-Channel Retargeting', b: 'Google, Meta, LinkedIn, and programmatic display in one plan.' },
      { t: 'Dynamic Product Ads', b: 'Show shoppers the exact products they viewed.' },
      { t: 'Email + Ads Sync', b: 'Coordinated remarketing across paid + email channels.' },
      { t: 'Frequency Capping', b: 'Avoid creep - smart caps based on intent signals.' },
      { t: 'Attribution', b: 'Multi-touch attribution that shows the real ROI of retargeting.' },
    ],
    benefits: ['Significantly lower cost-per-acquisition', 'Higher conversion rates on cold traffic', 'Cross-channel coordination', 'Server-side tracking for accuracy', 'Privacy-compliant audiences'],
  },
  {
    slug: 'reputation-management', category: 'marketing',
    name: 'Reputation Management',
    short: 'Review generation, monitoring, and brand trust building.',
    hero: '93% of buyers check reviews. We help you generate more 5-star reviews, monitor brand mentions, and turn happy customers into your best salespeople.',
    image: '/images/blog/photo-1556761175-b413da4baf72.jpg',
    offerings: [
      { t: 'Review Generation', b: 'Automated review request systems that drive consistent 5-star reviews.' },
      { t: 'Review Monitoring', b: '24/7 monitoring across Google, Yelp, Facebook, and industry sites.' },
      { t: 'Response Management', b: 'Professional, on-brand responses to every review - positive or negative.' },
      { t: 'Brand Mention Tracking', b: 'Real-time alerts for brand mentions across the web.' },
      { t: 'Reputation Recovery', b: 'Strategic SEO and content to push down negative content.' },
      { t: 'Review Marketing', b: 'Showcase reviews on your site, ads, and social channels.' },
    ],
    benefits: ['More 5-star reviews, faster', 'Real-time review monitoring', 'Professional response management', 'Brand mention tracking', 'Recovery strategies for negative content'],
  },
  {
    slug: 'media-placement-buying', category: 'marketing',
    name: 'Media Placement & Buying',
    short: 'Strategic ad buying across digital and traditional channels.',
    hero: 'Strategic media buying across digital, TV, radio, print, and out-of-home. We negotiate, place, and measure - so you get more reach for less spend.',
    image: '/images/blog/photo-1611532736597-de2d4265fba3.jpg',
    offerings: [
      { t: 'Media Planning', b: 'Channel mix recommendations based on audience and budget.' },
      { t: 'Negotiation', b: 'Better rates because we buy in volume across clients.' },
      { t: 'Traditional Media', b: 'TV, radio, print, OOH - placed and trafficked.' },
      { t: 'Digital Media', b: 'Programmatic, OTT, podcast, and direct publisher buys.' },
      { t: 'Trafficking & QA', b: 'Tag management, creative trafficking, and pre-flight QA.' },
      { t: 'Post-Buy Analysis', b: 'Delivery audits, brand lift studies, and ROI reporting.' },
    ],
    benefits: ['Better rates through scale', 'Cross-channel planning and buying', 'Independent and unbiased', 'Detailed post-buy reporting', '19 years of buyer relationships'],
  },

  // ── CREATIVE ─────────────────────────────────────────
  {
    slug: 'photography-videography', category: 'creative',
    name: 'Photography & Videography',
    short: 'Professional visuals including job sites, branding, and before/after content.',
    hero: 'Original photography and videography that brings your brand to life. On-location shoots for products, people, places, and proof.',
    image: '/images/blog/photo-1502920917128-1aa500764cbd.jpg',
    offerings: [
      { t: 'Brand Photography', b: 'Authentic lifestyle and headshot photography for your team and brand.' },
      { t: 'Product Photography', b: 'Studio and lifestyle product shoots - eCommerce ready.' },
      { t: 'Project Documentation', b: 'Before/after, job site, and case study photography.' },
      { t: 'Commercial Video', b: 'Brand films, explainers, and commercials - concept to delivery.' },
      { t: 'Drone & Aerial', b: 'FAA-licensed drone pilots for aerial photography and video.' },
      { t: 'Event Coverage', b: 'Grand openings, conferences, and company milestones.' },
    ],
    benefits: ['In-house photo and video team', 'FAA-licensed drone operators', 'Studio and on-location capabilities', 'Fast turnaround on edits', 'Rights to all final assets'],
  },
  {
    slug: 'video-creation-editing', category: 'creative',
    name: 'Video Creation & Editing',
    short: 'Promotional, testimonial, and ad videos.',
    hero: 'From concept to cut. We script, shoot, and edit promotional, testimonial, and ad videos that actually move the needle.',
    image: '/images/blog/photo-1574717024653-61fd2cf4d44d.jpg',
    offerings: [
      { t: 'Promotional Videos', b: 'Brand and product videos for web, social, and ads.' },
      { t: 'Testimonial Videos', b: 'Authentic customer story shoots - full production.' },
      { t: 'Social Cutdowns', b: 'Vertical, square, and TikTok-native edits from existing footage.' },
      { t: 'Animation & Motion', b: 'Explainer animation, kinetic type, and motion graphics.' },
      { t: 'Color & Sound', b: 'Professional color grading, audio mix, and voiceover.' },
      { t: 'Subtitles & Localization', b: 'Captions, subtitles, and multi-language versions.' },
    ],
    benefits: ['Full-production capabilities', 'In-house editors and motion designers', 'Multi-format delivery', 'Music licensing handled', 'Fast turnaround on revisions'],
  },
  {
    slug: 'voiceover', category: 'creative',
    name: 'Voiceover',
    short: 'Professional voiceovers for ads and branding.',
    hero: 'Professional voiceover talent for ads, explainers, IVR, and brand content. Studio-quality audio, fast turnaround.',
    image: '/images/blog/photo-1590602847861-f357a9332bbc.jpg',
    offerings: [
      { t: 'Talent Casting', b: 'Curated talent matched to your brand and audience.' },
      { t: 'Multi-Language', b: 'Native speakers across 30+ languages.' },
      { t: 'Studio Recording', b: 'Professional, treated rooms for broadcast-quality audio.' },
      { t: 'Audio Editing', b: 'EQ, compression, mastering - radio and broadcast ready.' },
      { t: 'Music Licensing', b: 'Cleared music beds and stings for your spots.' },
      { t: 'IVR & On-Hold', b: 'Phone tree, auto-attendant, and on-hold messaging.' },
    ],
    benefits: ['Curated, on-brand talent', 'Broadcast-quality audio', 'Multi-language capable', '24-48 hour turnaround typical', 'Music + voice + edit all in-house'],
  },
  {
    slug: 'graphic-design', category: 'creative',
    name: 'Graphic Design',
    short: 'Ads, social graphics, and marketing materials.',
    hero: 'Smart, distinctive graphic design across every channel. Ads, social, web graphics, and marketing collateral that look as good as they perform.',
    image: '/images/blog/photo-1626785774573-4b799315345d.jpg',
    offerings: [
      { t: 'Ad Creative', b: 'Static and animated ads across Google, Meta, LinkedIn, and TikTok.' },
      { t: 'Social Graphics', b: 'Daily posts, carousels, stories, and reels graphics on brand.' },
      { t: 'Marketing Collateral', b: 'One-pagers, sales decks, brochures, and case studies.' },
      { t: 'Infographics', b: 'Data visualizations and explainer infographics.' },
      { t: 'Email Templates', b: 'On-brand, responsive email templates.' },
      { t: 'Brand Guidelines', b: 'Living style guides so every channel looks like one company.' },
    ],
    benefits: ['Senior designers, not interns', 'On-brand across every channel', 'Conversion-aware design', 'Quick turnaround on production', 'Unlimited revisions on retainer'],
  },
  {
    slug: 'logo-design-branding', category: 'creative',
    name: 'Logo Design & Branding',
    short: 'Full brand identity systems and positioning.',
    hero: 'Brand identity systems built to last. Strategy, naming, logos, and design systems that work across every channel - for decades, not seasons.',
    image: '/images/blog/photo-1573164713714-d95e436ab8d6.jpg',
    offerings: [
      { t: 'Brand Strategy', b: 'Positioning, audience, and messaging architecture before design begins.' },
      { t: 'Naming', b: 'Distinctive, available, defensible names for brands and products.' },
      { t: 'Logo Design', b: 'Custom logo systems with primary, secondary, and responsive marks.' },
      { t: 'Visual Identity', b: 'Color, typography, iconography, and photography direction.' },
      { t: 'Brand Guidelines', b: 'Comprehensive guidelines documents your whole team can use.' },
      { t: 'Brand Rollout', b: 'Templates, collateral, and asset libraries for launch.' },
    ],
    benefits: ['Strategy-led, not aesthetic-only', 'Trademark research included', 'Full identity systems, not just a logo', 'Comprehensive guidelines documents', 'Asset libraries for your team'],
  },
  {
    slug: 'business-cards-print', category: 'creative',
    name: 'Business Cards, Print & More',
    short: 'Print collateral including flyers, brochures, and direct mail.',
    hero: 'Print isn\'t dead - it\'s just more powerful when done well. Business cards, brochures, direct mail, signage, and vehicle wraps.',
    image: '/images/blog/photo-1590602847861-f357a9332bbc.jpg',
    offerings: [
      { t: 'Business Cards', b: 'Standard, premium, foil, and letterpress business cards.' },
      { t: 'Brochures & Flyers', b: 'Tri-fold, bi-fold, sales sheets, and rack cards.' },
      { t: 'Direct Mail', b: 'Postcards, EDDM, and targeted direct mail campaigns.' },
      { t: 'Signage', b: 'Yard signs, banners, trade show, and interior signage.' },
      { t: 'Vehicle Graphics', b: 'Full and partial wraps, magnets, and decals.' },
      { t: 'Packaging', b: 'Custom packaging design, boxes, and labels.' },
    ],
    benefits: ['Design + print management', 'Vetted print partners nationwide', 'Direct mail list sourcing', 'On-time delivery', 'Eco-friendly options available'],
  },

  // ── BUSINESS & GROWTH ─────────────────────────────────────────
  {
    slug: 'growth-hacking', category: 'growth',
    name: 'Growth Hacking',
    short: 'Rapid experimentation, funnel optimization, and scaling strategies.',
    hero: 'Growth hacking is not magic - it\'s a system. Rapid experimentation, funnel optimization, and disciplined scaling for ambitious teams.',
    image: '/images/blog/photo-1460925895917-afdab827c52f.jpg',
    offerings: [
      { t: 'Experimentation Framework', b: 'Build a backlog, prioritize by impact/ease, and ship 4+ tests per month.' },
      { t: 'Funnel Optimization', b: 'CRO across acquisition, activation, retention, and referral.' },
      { t: 'Onboarding', b: 'Activation flows that turn signups into engaged users.' },
      { t: 'Viral Loops', b: 'Referral programs, sharing mechanics, and network effects.' },
      { t: 'Retention Plays', b: 'Lifecycle messaging, win-back, and reactivation systems.' },
      { t: 'Analytics & Cohorts', b: 'Cohort analysis, LTV modeling, and growth dashboards.' },
    ],
    benefits: ['Disciplined testing framework', 'Cross-functional execution', 'Senior growth strategists', 'Tied to revenue, not vanity metrics', 'Quarterly business reviews included'],
  },
  {
    slug: 'business-consulting', category: 'growth',
    name: 'Business Consulting',
    short: 'Marketing strategy, KPI tracking, and operational consulting.',
    hero: 'Strategic consulting for marketing-led growth. Positioning, KPI architecture, org design, and operational excellence.',
    image: '/images/blog/photo-1573164713714-d95e436ab8d6.jpg',
    offerings: [
      { t: 'Marketing Strategy', b: 'Annual planning, channel mix, and growth modeling.' },
      { t: 'KPI Architecture', b: 'North star metrics, leading indicators, and dashboards.' },
      { t: 'Org Design', b: 'In-house vs agency, role design, and team structure.' },
      { t: 'Tech Stack Audits', b: 'CRM, MarTech, and analytics stack reviews.' },
      { t: 'M&A Diligence', b: 'Marketing diligence for acquirers and acquirees.' },
      { t: 'Fractional CMO', b: 'Senior marketing leadership without the full-time cost.' },
    ],
    benefits: ['19 years of agency leadership experience', 'Senior strategists only', 'Engagements tied to business KPIs', 'Cross-functional perspective', 'Available as fractional CMO'],
  },
  {
    slug: 'managed-it-services', category: 'growth',
    name: 'Managed IT Services',
    short: 'IT support, security, cloud solutions, and infrastructure management.',
    hero: 'Managed IT services that keep your business running. Help desk, security, cloud, and infrastructure - backed by humans, not chatbots.',
    image: '/images/blog/photo-1498050108023-c5249f4df085.jpg',
    offerings: [
      { t: 'Help Desk', b: 'Responsive support for your team - by phone, email, or chat.' },
      { t: 'Cybersecurity', b: 'Endpoint protection, MFA, phishing training, and SOC monitoring.' },
      { t: 'Cloud & Backup', b: 'Microsoft 365, Google Workspace, and automated cloud backup.' },
      { t: 'Network & Wi-Fi', b: 'Business-grade networking, firewalls, and wireless.' },
      { t: 'Patching & Updates', b: 'Automated OS and software patching across your fleet.' },
      { t: 'vCIO Services', b: 'Fractional IT leadership and roadmap planning.' },
    ],
    benefits: ['Predictable monthly pricing', 'US-based help desk', 'Proactive security monitoring', 'Cloud + on-prem support', 'vCIO services included'],
  },
];

// Helpers
export function getServiceBySlug(slug) {
  return SERVICES.find((s) => s.slug === slug);
}

export function getServicesByCategory(catKey) {
  return SERVICES.filter((s) => s.category === catKey);
}

export const SERVICE_DEFAULTS = { PROCESS_DEFAULT, STAT_BAR };
